import { Button, Table, Tooltip } from "antd";
import { Breakpoint } from "antd";
import { Link } from "react-router-dom";
import { EyeTwoTone, FileExcelTwoTone } from "@ant-design/icons";
import { WarehouseSummary } from "../../../../../models/Warehouse";
import WarehouseService from "../../../../../services/WarehouseService";
import { useLanguage } from "../../../../../context/LanguageContext";
import { getWarehouseReferenceColor } from "../../../../../common/warehouseColorReferences";

interface WarehousesTableProps {
  warehouses: WarehouseSummary[];
  isMobile: boolean;
}

const WarehousesTable = (props: WarehousesTableProps) => {
  const { language } = useLanguage();
  const iconButtonStyle = { fontSize: 20, cursor: "pointer" };
  const columns = [
    {
      title: "",
      key: "idx",
      responsive: ["lg"] as Breakpoint[],
      render: (item: WarehouseSummary) => item.idx,
    },
    {
      title: language === "es" ? "Nombre" : "Name",
      key: "name",
      render: (item: WarehouseSummary) => {
        const warehouseName = item.name || "";
        const color = getWarehouseReferenceColor(warehouseName);

        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: color,
                display: "inline-block",
              }}
            />
            <span style={{ color, fontWeight: 600 }}>{warehouseName || "-"}</span>
          </span>
        );
      },
    },
    {
      title: language === "es" ? "Número" : "Number",
      key: "number",
      responsive: ["lg"] as Breakpoint[],
      render: (item: WarehouseSummary) => item.warehouse_number,
    },
    {
      title: language === "es" ? "Pasillos" :"Corridors",
      key: "corridors",
      align: "center" as const,
      responsive: ["lg"] as Breakpoint[],
      render: (item: WarehouseSummary) => item.corridors.toString(),
    },
    {
      title: language === "es" ? "Dispositivos" :"Devices",
      key: "devices",
      align: "center" as const,
      responsive: ["lg"] as Breakpoint[],
      render: (item: WarehouseSummary) => item.devices.toString(),
    },
    {
      title: language === "es" ? "Posiciones Totales" :"Total Positions",
      key: "positions",
      responsive: ["lg"] as Breakpoint[],
      render: (item: WarehouseSummary) => item.positions.toString(),
    },
    {
      title: language === "es" ? "Posiciones vacías" : "Empty Positions",
      key: "empty_positions",
      responsive: ["lg"] as Breakpoint[],
      render: (item: WarehouseSummary) => item.empty_positions.toString(),
    },
    {
      title: language === "es" ? "Posiciones llenas" :"Full Positions",
      key: "full_positions",
      responsive: ["lg"] as Breakpoint[],
      render: (item: WarehouseSummary) => item.full_positions.toString(),
    },
    {
      title: language === "es" ? "Posiciones Inexploradas" : "Unexplored Positions",
      key: "unknown_positions",
      responsive: ["lg"] as Breakpoint[],
      render: (item: WarehouseSummary) => item.unknown_positions.toString(),
    },
    {
      title: language === "es" ? "Detalles" :"Details",
      key: "details",
      align: "center" as const,
      render: (item: WarehouseSummary) => {
        const icon = <EyeTwoTone style={iconButtonStyle} twoToneColor="#1677ff" />;

        return (
          <Link to={`${item.idx}`} aria-label={language === "es" ? "Detalles" : "Details"}>
            {icon}
          </Link>
        );
      },
    },
    {
      title: "CSV",
      key: "csv",
      align: "center" as const,
      responsive: ["lg"] as Breakpoint[],
      render: (item: WarehouseSummary) => (
        <Tooltip title="CSV">
          <FileExcelTwoTone
            style={iconButtonStyle}
            twoToneColor="#52c41a"
            onClick={() => WarehouseService.exportCSV(String(item.idx))}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={props.warehouses}
      rowKey="id"
      scroll={{ x: true }}
      size="small"
      pagination={false}
    />
  );
};

export default WarehousesTable;